"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { SearchCheck, KeyRound, FileText, FolderTree, Cpu, Network, Code2, BookOpen, LineChart, MousePointerClick, TrendingUp, ArrowRight, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface ServiceItem {
  id: string;
  number: string;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  link?: {
    to: string;
    label: string;
  };
}
const services: ServiceItem[] = [{
  id: "ecommerce-seo-audits",
  number: "01",
  title: "Ecommerce SEO audits",
  description: "A comprehensive audit of technical health, site architecture, product and category pages, content quality, and competitor positioning. The deliverable is a prioritised list of fixes ordered by commercial impact, putting revenue-critical categories first.",
  icon: SearchCheck,
  link: {
    to: "/SeoAuditsPage",
    label: "Explore SEO audits"
  }
}, {
  id: "product-category-keyword-research",
  number: "02",
  title: "Product and category keyword research",
  description: "Targeted research covering product-level, category-level, brand, non-brand, and long-tail search terms to match how customers actually search. The output is a clear keyword-to-page map that guides optimisation and reveals where new collections or content pages should be created.",
  icon: KeyRound
}, {
  id: "product-page-optimisation",
  number: "03",
  title: "Product page optimisation",
  description: "Optimising titles, H1 headings, unique descriptions, metadata, imagery, alt text, internal links, and reviews integration so each product page ranks for relevant searches and gives buyers confidence to add items to their basket.",
  icon: FileText
}, {
  id: "category-collection-seo",
  number: "04",
  title: "Category and collection page SEO",
  description: "Optimising category naming, introductory copy, filter handling, pagination, and internal linking so your category and collection pages capture high-intent, non-branded search traffic that converts.",
  icon: FolderTree
}, {
  id: "technical-ecommerce-seo",
  number: "05",
  title: "Technical ecommerce SEO",
  description: "Resolving crawl efficiency, canonical tags, duplicate URLs, structured data validation, Core Web Vitals, and mobile usability across dynamic store catalogues and complex filter systems.",
  icon: Cpu,
  link: {
    to: "/TechnicalSeoPage",
    label: "Learn about technical SEO"
  }
}, {
  id: "site-architecture-linking",
  number: "06",
  title: "Site architecture and internal linking",
  description: "Restructuring category hierarchies and internal linking paths so domain authority flows directly into your highest-value commercial pages, creating a scalable taxonomy that grows alongside your catalogue.",
  icon: Network
}, {
  id: "structured-data-schema",
  number: "07",
  title: "Structured data and product schema recommendations",
  description: "Implementation notes for Product, Offer, Review, and Breadcrumb schema, configured via apps or plugins where possible, helping search engines understand product pages and enabling rich results where eligible.",
  icon: Code2
}, {
  id: "ecommerce-content-strategy",
  number: "08",
  title: "Ecommerce content strategy",
  description: "Designing buying guides, FAQs, product comparisons, and 'how to choose' content directly connected to commercial products and collections, capturing search intent earlier in the buying cycle.",
  icon: BookOpen
}, {
  id: "competitor-research-authority",
  number: "09",
  title: "Competitor research and authority building",
  description: "Benchmarking against search competitors across category visibility, content gaps, internal linking, and backlink profiles to identify realistic, relevant link-building opportunities without overpromising.",
  icon: LineChart
}, {
  id: "conversion-oriented-seo",
  number: "10",
  title: "Conversion-oriented SEO recommendations",
  description: "Improving on-page trust signals, messaging, layout, and user experience to support transactions. SEO brings the right people to the right page; these recommendations help more of them complete their purchase.",
  icon: MousePointerClick
}, {
  id: "measurement-ongoing-optimisation",
  number: "11",
  title: "Measurement and ongoing optimisation",
  description: "Regular review cycles analysing Google Search Console data, analytics, and ecommerce metrics to refine strategy, identify emerging product demand, and prioritise continuous improvements.",
  icon: TrendingUp,
  link: {
    to: "/MonthlySeoServicesPage",
    label: "View monthly SEO services"
  }
}];
export default function EcommercePillars() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="ecommerceseopillars" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-b border-border transition-colors duration-200">
      <div className="max-w-7xl mx-auto">
        {/* Section Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge variant="outline" className="px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Consulting Services
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            Ecommerce SEO services I provide
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed pt-1">
            My ecommerce SEO consulting is modular: whether you need a one-off
            technical audit, focused project work such as a platform migration,
            or ongoing monthly consulting, every engagement is tailored to your
            store&apos;s commercial priorities.
          </p>
        </div>

        {/* Eleven Services Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.08
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
          {services.map((service, i) => {
          const Icon = service.icon;
          return <motion.div key={service.id} data-index={i} variants={itemVariants} className="flex flex-col">
                <Card className="h-full bg-card text-card-foreground border border-border/80 shadow-xs hover:border-primary/40 transition-all rounded-lg flex flex-col justify-between p-6">
                  <CardHeader className="p-0 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <Icon className="size-5" />
                      </div>
                      <span className="text-xs font-mono font-bold px-2 py-0.5 rounded bg-primary/10 text-primary">
                        {service.number}
                      </span>
                    </div>
                    <CardTitle className="text-lg sm:text-xl font-semibold text-card-foreground font-default">
                      {service.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="p-0 pt-3 flex-1 flex flex-col justify-between">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>

                    {service.link && <div className="pt-4 mt-4 border-t border-border/60">
                        <Link to={service.link.to} className="text-primary underline underline-offset-4 hover:text-primary/80 text-xs sm:text-sm font-medium inline-flex items-center gap-1">
                          {service.link.label}
                          <ArrowRight className="size-3.5" />
                        </Link>
                      </div>}
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Closing Consultation & Audit CTA Block */}
        <div className="mt-16 bg-muted/40 border border-border rounded-2xl p-6 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left max-w-2xl">
            <h3 className="text-xl sm:text-2xl font-semibold text-foreground font-default">
              Ready to discuss your store&apos;s organic search priorities?
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              I work directly with UK online stores and international
              English-speaking retailers to increase organic visibility on the
              product and category pages that matter most.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full md:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                Book a Free SEO Consultation
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/ContactPage?topic=free-seo-audit">
                Get a Free SEO Audit
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}